import { StatCard } from '@/components/StatCard';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import {
  IconAction,
  InvoiceRow,
  LinkedDetails,
} from '@/components/invoices/InvoiceRow';
import { LinkPicker } from '@/components/invoices/LinkPicker';
import { UpdateInvoiceDialog } from '@/components/UpdateInvoiceDialog';
import { UploadInvoiceDialog } from '@/components/UploadInvoiceDialog';
import { useDefaultSite } from '@/hooks/useDefaultSite';
import { useInvoices } from '@/hooks/useInvoices';
import { useLinkDetail } from '@/hooks/useLinkDetail';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { getUserRole } from '@/lib/auth/token';
import { invoiceClient } from '@/lib/api/ts-rest';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Fuse from 'fuse.js';
import {
  Calendar as CalendarIcon,
  Download,
  FileText,
  Pencil,
  Search,
  Trash2,
  Unlink,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { Invoice } from '@octave/contracts';
import { downloadFileFromUrl } from '@/lib/api/file-download';

const INVOICE_TYPE_OPTIONS = [
  { value: 'PHONE_PLAN', label: 'Forfaits mobiles' },
  { value: 'EQUIPMENT', label: 'Équipements' },
];

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 2019 }, (_, i) => 2020 + i);

export default function InvoicesView() {
  const { data: invoicesData, isLoading, error, refetch } = useInvoices();
  const { data: organization } = useCurrentOrganization();
  const userRole = getUserRole();
  // Billing center picked in the top bar (undefined = all centers).
  const { currentSite } = useDefaultSite();
  const linkDetail = useLinkDetail();

  const [uploadKind, setUploadKind] = useState<'INVOICE' | 'DETAIL' | null>(
    null
  );
  const [updateDialog, setUpdateDialog] = useState<{
    open: boolean;
    invoice: Invoice | null;
  }>({ open: false, invoice: null });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    invoice: Invoice | null;
  }>({ open: false, invoice: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Filters
  const [searchParams] = useSearchParams();
  const initialMonthFilter = useMemo(() => {
    const monthParam = searchParams.get('month');
    if (!monthParam) return null;
    const match = /^(\d{4})-(\d{2})$/.exec(monthParam);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    if (Number.isNaN(year) || Number.isNaN(month) || month < 0 || month > 11) {
      return null;
    }
    return {
      fromMonth: month,
      fromYear: year,
      toMonth: month,
      toYear: year,
    };
  }, [searchParams]);

  const [searchText, setSearchText] = useState('');
  const [dateFilter, setDateFilter] = useState<string>(
    initialMonthFilter ? 'month-range' : 'all',
  );
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [customDateRange, setCustomDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({ from: undefined, to: undefined });
  const [fromMonth, setFromMonth] = useState<{
    month: number;
    year: number;
  } | null>(null);
  const [monthRange, setMonthRange] = useState<{
    fromMonth: number;
    fromYear: number;
    toMonth: number;
    toYear: number;
  } | null>(initialMonthFilter);

  // Pagination
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);

  // Flatten all invoices into a single list
  const allInvoices = useMemo(() => {
    if (!invoicesData) return [];
    const phonePlan = Object.values(
      invoicesData.phonePlanInvoices || {}
    ).flat();
    const equipment = invoicesData.equipmentInvoices || [];
    return [...phonePlan, ...equipment].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [invoicesData]);

  // Scope to the billing center selected in the top bar. No selection (all
  // centers) → every invoice. Stats, search and filters all run on this set.
  const scopedInvoices = useMemo(() => {
    if (!currentSite) return allInvoices;
    return allInvoices.filter((inv) =>
      inv.sites.some((s) => s.id === currentSite.id)
    );
  }, [allInvoices, currentSite]);

  // Link-picker options, derived from the top-level rows (center-scoped).
  const invoiceOptions = useMemo(
    () =>
      scopedInvoices
        .filter((inv) => inv.kind === 'INVOICE')
        .map((inv) => ({
          id: inv.id,
          label: inv.reference,
          sublabel: format(new Date(inv.date), 'dd/MM/yyyy'),
        })),
    [scopedInvoices]
  );
  const unlinkedDetailOptions = useMemo(
    () =>
      scopedInvoices
        .filter((inv) => inv.kind === 'DETAIL' && inv.linkedInvoiceId === null)
        .map((inv) => ({
          id: inv.id,
          label: inv.reference,
          sublabel: format(new Date(inv.date), 'dd/MM/yyyy'),
        })),
    [scopedInvoices]
  );

  // Fuse.js for search
  const fuse = useMemo(
    () =>
      new Fuse(scopedInvoices, {
        keys: ['reference', 'sites.name'],
        threshold: 0.3,
      }),
    [scopedInvoices]
  );

  // Stats
  const stats = useMemo(() => {
    if (!scopedInvoices.length) return null;
    const now = new Date();
    const thisMonthCount = scopedInvoices.filter((inv) => {
      const d = new Date(inv.date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    const phonePlanCount = scopedInvoices.filter((inv) => inv.type === 'PHONE_PLAN').length;
    const equipmentCount = scopedInvoices.filter((inv) => inv.type === 'EQUIPMENT').length;
    return {
      total: scopedInvoices.length,
      thisMonth: thisMonthCount,
      phonePlanCount,
      equipmentCount,
    };
  }, [scopedInvoices]);

  // Filter by date
  const getDateRange = useCallback((): { start: Date; end: Date } | null => {
    if (!dateFilter || dateFilter === 'all') return null;
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    if (dateFilter === 'custom') {
      if (!customDateRange.from || !customDateRange.to) return null;
      return { start: customDateRange.from, end: customDateRange.to };
    }

    if (dateFilter === 'from-month') {
      if (!fromMonth) return null;
      return {
        start: new Date(fromMonth.year, fromMonth.month, 1),
        end: now,
      };
    }

    if (dateFilter === 'month-range') {
      if (!monthRange) return null;
      return {
        start: new Date(monthRange.fromYear, monthRange.fromMonth, 1),
        end: new Date(monthRange.toYear, monthRange.toMonth + 1, 0),
      };
    }

    switch (dateFilter) {
      case 'this-month':
        return {
          start: new Date(currentYear, currentMonth, 1),
          end: new Date(currentYear, currentMonth + 1, 0),
        };
      case 'last-month':
        return {
          start: new Date(currentYear, currentMonth - 1, 1),
          end: new Date(currentYear, currentMonth, 0),
        };
      case 'last-3-months':
        return {
          start: new Date(currentYear, currentMonth - 2, 1),
          end: new Date(currentYear, currentMonth + 1, 0),
        };
      case 'last-6-months':
        return {
          start: new Date(currentYear, currentMonth - 5, 1),
          end: new Date(currentYear, currentMonth + 1, 0),
        };
      case 'this-year':
        return {
          start: new Date(currentYear, 0, 1),
          end: new Date(currentYear, 11, 31),
        };
      default:
        return null;
    }
  }, [dateFilter, customDateRange, fromMonth, monthRange]);

  // Combined filtering
  const filteredInvoices = useMemo(() => {
    let result = scopedInvoices;

    // Search
    if (searchText) {
      result = fuse.search(searchText).map((r) => r.item);
    }

    // Type filter
    if (typeFilters.length > 0) {
      result = result.filter(
        (inv) => inv.type !== null && typeFilters.includes(inv.type)
      );
    }

    // Date filter
    const range = getDateRange();
    if (range) {
      result = result.filter((inv) => {
        const d = new Date(inv.date);
        return d >= range.start && d <= range.end;
      });
    }

    return result;
  }, [scopedInvoices, searchText, typeFilters, getDateRange, fuse]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / pageSize));
  const paginatedInvoices = filteredInvoices.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // Group by month for display
  const groupedByMonth = useMemo(() => {
    const groups: Record<string, Invoice[]> = {};
    for (const inv of paginatedInvoices) {
      const d = new Date(inv.date);
      const key = format(d, 'MMMM yyyy', { locale: fr });
      if (!groups[key]) groups[key] = [];
      groups[key].push(inv);
    }
    return groups;
  }, [paginatedInvoices]);

  const hasActiveFilters =
    searchText || typeFilters.length > 0 || (dateFilter && dateFilter !== 'all');

  const handleClearFilters = () => {
    setSearchText('');
    setTypeFilters([]);
    setDateFilter('all');
    setCustomDateRange({ from: undefined, to: undefined });
    setFromMonth(null);
    setMonthRange(null);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setPage(1);
  };

  const handleTypeFilterChange = (value: string, checked: boolean) => {
    const newFilters = checked
      ? [...typeFilters, value]
      : typeFilters.filter((t) => t !== value);
    setTypeFilters(newFilters);
    setPage(1);
  };

  const handleDownload = async (invoice: Invoice) => {
    try {
      if (!organization) {
        toast.error('Organisation non trouvée');
        return;
      }
      const downloadRes = await invoiceClient.getInvoiceDownloadUrl({
        params: { organizationId: organization.id, invoiceId: invoice.id },
      });
      if (downloadRes.status !== 200) throw new Error('Download failed');
      const { downloadUrl } = downloadRes.body;
      // Extract extension from publicId (e.g. "invoices/orgId/uuid.pdf" → ".pdf")
      const ext = invoice.publicId.includes('.')
        ? invoice.publicId.substring(invoice.publicId.lastIndexOf('.'))
        : '.pdf';
      const downloadName = `${invoice.reference || 'facture'}${ext}`;

      await downloadFileFromUrl(downloadUrl, downloadName);
    } catch {
      toast.error('Erreur lors du téléchargement de la facture');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.invoice || !organization) return;
    setIsDeleting(true);
    try {
      const deleteRes = await invoiceClient.deleteInvoice({
        params: {
          organizationId: organization.id,
          invoiceId: deleteDialog.invoice.id,
        },
      });
      if (deleteRes.status !== 200) throw new Error('Delete failed');
      toast.success('Facture supprimée avec succès');
      refetch();
      setDeleteDialog({ open: false, invoice: null });
    } catch {
      toast.error('Erreur lors de la suppression de la facture');
    } finally {
      setIsDeleting(false);
    }
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-8">
        <div>
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-4 w-64 mt-2" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-56" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-2">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Factures</h1>
          <p className="text-sm text-slate-500 mt-1">
            Gérez vos factures récurrentes et équipements
          </p>
        </div>
        <div className="flex flex-col items-center justify-center py-16 text-slate-400">
          <FileText className="h-10 w-10 mb-3 text-slate-300" />
          <p className="text-sm font-medium text-red-500">
            Une erreur est survenue lors du chargement des factures
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
            className="mt-4 bg-white border-slate-200"
          >
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Factures</h1>
          <p className="text-sm text-slate-500 mt-1">
            Gérez vos factures récurrentes et équipements
          </p>
        </div>
        {userRole === 'ADMIN' && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUploadKind('INVOICE')}
              className="bg-white border-slate-200 text-slate-600 gap-1.5"
            >
              <Upload className="h-4 w-4" />
              Uploader une facture
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUploadKind('DETAIL')}
              className="bg-white border-slate-200 text-slate-600 gap-1.5"
            >
              <Upload className="h-4 w-4" />
              Uploader un détail
            </Button>
          </div>
        )}
      </div>

      {/* Stat Cards */}
      {stats && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={FileText}
            iconBg="bg-emerald-50"
            iconColor="text-emerald-600"
            label="Total factures"
            value={stats.total}
          >
            <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
              <span>Forfaits : {stats.phonePlanCount}</span>
              <span>Équipements : {stats.equipmentCount}</span>
            </div>
          </StatCard>

          <StatCard
            icon={CalendarIcon}
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
            label="Ce mois-ci"
            value={stats.thisMonth}
          >
            <p className="mt-3 text-xs text-slate-500">
              Factures du mois en cours
            </p>
          </StatCard>
        </div>
      )}

      {/* Filters bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full sm:flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Rechercher une facture..."
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9 bg-white border-slate-200"
          />
        </div>

        {/* Type filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="bg-white border-slate-200 text-slate-600 gap-1.5"
            >
              Type
              {typeFilters.length > 0 && (
                <span className="bg-emerald-100 text-emerald-700 rounded-full px-1.5 text-[10px] font-bold">
                  {typeFilters.length}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            {INVOICE_TYPE_OPTIONS.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={typeFilters.includes(option.value)}
                onCheckedChange={(checked) =>
                  handleTypeFilterChange(option.value, checked)
                }
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Date filter */}
        <Select
          value={dateFilter}
          onValueChange={(v) => {
            setDateFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[180px] bg-white border-slate-200 text-sm">
            <SelectValue placeholder="Période" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month-range">Plage mois/année</SelectItem>
            <SelectItem value="all">Toutes les dates</SelectItem>
            <SelectItem value="this-month">Ce mois-ci</SelectItem>
            <SelectItem value="last-month">Mois dernier</SelectItem>
            <SelectItem value="last-3-months">3 derniers mois</SelectItem>
            <SelectItem value="last-6-months">6 derniers mois</SelectItem>
            <SelectItem value="this-year">Cette année</SelectItem>
            <SelectItem value="from-month">À partir de...</SelectItem>
          </SelectContent>
        </Select>

        {/* From month selects */}
        {dateFilter === 'from-month' && (
          <div className="flex items-center gap-2">
            <Select
              value={fromMonth ? `${fromMonth.month}` : ''}
              onValueChange={(v) => {
                setFromMonth((prev) => ({
                  month: Number(v),
                  year: prev?.year ?? currentYear,
                }));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[130px] bg-white border-slate-200 text-sm">
                <SelectValue placeholder="Mois" />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={i} value={`${i}`}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={fromMonth ? `${fromMonth.year}` : ''}
              onValueChange={(v) => {
                setFromMonth((prev) => ({
                  month: prev?.month ?? 0,
                  year: Number(v),
                }));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[100px] bg-white border-slate-200 text-sm">
                <SelectValue placeholder="Année" />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => (
                  <SelectItem key={y} value={`${y}`}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Month range selects */}
        {dateFilter === 'month-range' && (
          <div className="flex items-center gap-2 flex-wrap">
            <Select
              value={monthRange ? `${monthRange.fromMonth}` : ''}
              onValueChange={(v) => {
                setMonthRange((prev) => ({
                  fromMonth: Number(v),
                  fromYear: prev?.fromYear ?? currentYear,
                  toMonth: prev?.toMonth ?? new Date().getMonth(),
                  toYear: prev?.toYear ?? currentYear,
                }));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[120px] bg-white border-slate-200 text-sm">
                <SelectValue placeholder="De mois" />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={i} value={`${i}`}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={monthRange ? `${monthRange.fromYear}` : ''}
              onValueChange={(v) => {
                setMonthRange((prev) => ({
                  fromMonth: prev?.fromMonth ?? 0,
                  fromYear: Number(v),
                  toMonth: prev?.toMonth ?? new Date().getMonth(),
                  toYear: prev?.toYear ?? currentYear,
                }));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[90px] bg-white border-slate-200 text-sm">
                <SelectValue placeholder="Année" />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => (
                  <SelectItem key={y} value={`${y}`}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-400">→</span>
            <Select
              value={monthRange ? `${monthRange.toMonth}` : ''}
              onValueChange={(v) => {
                setMonthRange((prev) => ({
                  fromMonth: prev?.fromMonth ?? 0,
                  fromYear: prev?.fromYear ?? currentYear,
                  toMonth: Number(v),
                  toYear: prev?.toYear ?? currentYear,
                }));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[120px] bg-white border-slate-200 text-sm">
                <SelectValue placeholder="À mois" />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={i} value={`${i}`}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={monthRange ? `${monthRange.toYear}` : ''}
              onValueChange={(v) => {
                setMonthRange((prev) => ({
                  fromMonth: prev?.fromMonth ?? 0,
                  fromYear: prev?.fromYear ?? currentYear,
                  toMonth: prev?.toMonth ?? new Date().getMonth(),
                  toYear: Number(v),
                }));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[90px] bg-white border-slate-200 text-sm">
                <SelectValue placeholder="Année" />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => (
                  <SelectItem key={y} value={`${y}`}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="text-slate-500 hover:text-slate-700 gap-1"
          >
            <X className="h-3.5 w-3.5" />
            Effacer
          </Button>
        )}
      </div>

      {/* Invoice list */}
      <div className="space-y-4">
        {paginatedInvoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <FileText className="h-10 w-10 mb-3 text-slate-300" />
            <p className="text-sm font-medium">Aucune facture trouvée</p>
            <p className="text-xs mt-1">Essayez de modifier vos filtres</p>
          </div>
        ) : (
          Object.entries(groupedByMonth).map(([month, invoices]) => (
            <div key={month} className="space-y-2">
              <div className="flex items-center gap-3 px-1">
                <h3 className="text-sm font-semibold text-slate-700 capitalize">
                  {month}
                </h3>
                <span className="text-xs text-slate-400">
                  {invoices.length} facture{invoices.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="space-y-2">
                {invoices.map((invoice) => {
                  const isDetail = invoice.kind === 'DETAIL';
                  const isAdmin = userRole === 'ADMIN';
                  return (
                    <InvoiceRow
                      key={invoice.id}
                      invoice={invoice}
                      actions={
                        <>
                          <IconAction
                            icon={Download}
                            label="Télécharger"
                            onClick={() => handleDownload(invoice)}
                          />
                          {isAdmin && isDetail && (
                            <LinkPicker
                              label="Lier"
                              items={invoiceOptions}
                              searchPlaceholder="Rechercher une facture…"
                              emptyText="Aucune facture"
                              onSelect={(invoiceId) =>
                                linkDetail.mutate({
                                  detailId: invoice.id,
                                  invoiceId,
                                })
                              }
                            />
                          )}
                          {isAdmin && !isDetail && (
                            <LinkPicker
                              label="Détails"
                              items={unlinkedDetailOptions}
                              searchPlaceholder="Rechercher un détail…"
                              emptyText="Aucun détail non lié"
                              onSelect={(detailId) =>
                                linkDetail.mutate({
                                  detailId,
                                  invoiceId: invoice.id,
                                })
                              }
                            />
                          )}
                          {isAdmin && !isDetail && (
                            <IconAction
                              icon={Pencil}
                              label="Modifier"
                              onClick={() =>
                                setUpdateDialog({ open: true, invoice })
                              }
                            />
                          )}
                          {isAdmin && (
                            <IconAction
                              icon={Trash2}
                              label="Supprimer"
                              danger
                              onClick={() =>
                                setDeleteDialog({ open: true, invoice })
                              }
                            />
                          )}
                        </>
                      }
                    >
                      {!isDetail &&
                        invoice.linkedDetails &&
                        invoice.linkedDetails.length > 0 && (
                          <LinkedDetails
                            details={invoice.linkedDetails}
                            renderActions={(detail) => (
                              <>
                                <IconAction
                                  icon={Download}
                                  label="Télécharger"
                                  onClick={() => handleDownload(detail)}
                                />
                                {isAdmin && (
                                  <IconAction
                                    icon={Unlink}
                                    label="Délier"
                                    onClick={() =>
                                      linkDetail.mutate({
                                        detailId: detail.id,
                                        invoiceId: null,
                                      })
                                    }
                                  />
                                )}
                              </>
                            )}
                          />
                        )}
                    </InvoiceRow>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {filteredInvoices.length > 0 && (
        <div className="flex items-center justify-between pt-2">
          <div className="hidden sm:flex items-center space-x-2">
            <p className="text-sm text-slate-500">Par page</p>
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => {
                setPageSize(Number(value));
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[70px] bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 20, 30, 40, 50].map((size) => (
                  <SelectItem key={size} value={`${size}`}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">
              {filteredInvoices.length} facture
              {filteredInvoices.length > 1 ? 's' : ''} · Page {page} /{' '}
              {totalPages}
            </span>
            <PaginationPrevious
              onClick={() => {
                if (page > 1) setPage(page - 1);
              }}
              aria-disabled={page <= 1}
              className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
              aria-label="Page précédente"
            />
            <PaginationNext
              onClick={() => {
                if (page < totalPages) setPage(page + 1);
              }}
              aria-disabled={page >= totalPages}
              className={
                page >= totalPages ? 'pointer-events-none opacity-50' : ''
              }
              aria-label="Page suivante"
            />
          </div>
        </div>
      )}

      {/* Upload Dialog (invoice or billing detail) */}
      <UploadInvoiceDialog
        open={uploadKind !== null}
        kind={uploadKind ?? 'INVOICE'}
        invoiceOptions={invoiceOptions}
        onOpenChange={(open) => !open && setUploadKind(null)}
        onUploadSuccess={() => {
          refetch();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, invoice: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer la facture</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer la facture{' '}
              <strong>{deleteDialog.invoice?.reference}</strong> ? Cette action
              est irréversible et supprimera également le fichier associé.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, invoice: null })}
              disabled={isDeleting}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Invoice Dialog */}
      <UpdateInvoiceDialog
        open={updateDialog.open}
        onOpenChange={(open) => setUpdateDialog({ open, invoice: null })}
        invoice={updateDialog.invoice}
        onUpdateSuccess={() => {
          refetch();
          setUpdateDialog({ open: false, invoice: null });
        }}
      />
    </div>
  );
}
